/* ==========================================================================
   GG · Family bundle builder
   Step 1: pick a print. Step 2: pick styles + sizes. Add all to cart as one
   Simple Bundles bundle, or as separate items.
   ========================================================================== */
(() => {
  if (customElements.get('hgg-bundle-builder')) return;

  const esc = (str) =>
    String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  class HggBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const d = this.dataset;
      this.cfg = {
        mode: d.cartMode || 'simple_bundles',
        labelFormat: d.labelFormat || '[product] - [size]',
        min: parseInt(d.minPieces, 10) || 1,
        max: parseInt(d.maxPieces, 10) || 12,
        moneyFormat: d.moneyFormat || '${{amount}}',
        tiers: [
          { qty: parseInt(d.tier1Qty, 10) || 0, pct: parseInt(d.tier1Pct, 10) || 0 },
          { qty: parseInt(d.tier2Qty, 10) || 0, pct: parseInt(d.tier2Pct, 10) || 0 }
        ].filter((t) => t.qty > 0 && t.pct > 0).sort((a, b) => a.qty - b.qty),
        tierOnSale: d.tierOnSale !== 'false'
      };

      try {
        this.t = JSON.parse(this.querySelector('template[data-strings]').innerHTML);
      } catch (e) {
        this.t = {};
      }

      this.el = {
        prints: Array.from(this.querySelectorAll('.hgg-bb__print')),
        cats: Array.from(this.querySelectorAll('.hgg-bb__cat')),
        stepPrints: this.querySelector('[data-step-prints]'),
        current: this.querySelector('[data-current-print]'),
        prompt: this.querySelector('[data-prompt]'),
        tabs: this.querySelector('[data-tabs]'),
        products: this.querySelector('[data-products]'),
        step2: this.querySelector('[data-step-2]'),
        summary: this.querySelector('[data-summary]'),
        summaryPrint: this.querySelector('[data-summary-print]'),
        lines: this.querySelector('[data-lines]'),
        empty: this.querySelector('[data-empty]'),
        tier: this.querySelector('[data-tier]'),
        tierMsg: this.querySelector('[data-tier-msg]'),
        tierFill: this.querySelector('[data-tier-fill]'),
        saveRow: this.querySelector('[data-save-row]'),
        saved: this.querySelector('[data-saved]'),
        savedAmount: this.querySelector('[data-saved-amount]'),
        save: this.querySelector('[data-save]'),
        total: this.querySelector('[data-total]'),
        add: this.querySelector('[data-add]'),
        addLabel: this.querySelector('[data-add-label]'),
        error: this.querySelector('[data-error]'),
        bar: this.querySelector('[data-bar]'),
        barInfo: this.querySelector('[data-bar-info]'),
        barBtn: this.querySelector('[data-bar-btn]'),
        overlay: this.querySelector('[data-overlay]'),
        close: this.querySelector('[data-sheet-close]')
      };

      this.mobileQuery = window.matchMedia('(max-width: 999px)');

      this.data = {};
      this.items = []; // one family set across all prints: [{ key, variantId, printId, print, product, size, variantTitle, price, compare, img, qty }]
      this.picked = {}; // productId -> variantId (size picked on a card, not yet added)
      this.group = 'all';
      this.printId = null;
      this.category = null;

      this.el.cats.forEach((btn) => {
        btn.addEventListener('click', () => this.selectCategory(btn.dataset.category, true));
        btn.addEventListener('keydown', (e) => this.onCatKey(e, btn));
      });

      this.el.prints.forEach((btn) => {
        btn.addEventListener('click', () => this.selectPrint(btn.dataset.print, true));
        btn.addEventListener('keydown', (e) => this.onPrintKey(e, btn));
      });

      this.el.tabs.addEventListener('click', (e) => {
        const tab = e.target.closest('[data-group]');
        if (!tab) return;
        this.group = tab.dataset.group;
        this.renderTabs();
        this.renderProducts();
      });

      this.el.products.addEventListener('click', (e) => this.onProductsClick(e));
      this.el.lines.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-remove]');
        if (btn) this.removeLine(btn.dataset.remove);
      });
      this.el.add.addEventListener('click', () => this.addToCart());
      // Mobile: the sticky bar opens the summary as a bottom sheet
      this.el.bar.addEventListener('click', () => this.openSheet());
      if (this.el.overlay) this.el.overlay.addEventListener('click', () => this.closeSheet());
      if (this.el.close) this.el.close.addEventListener('click', () => this.closeSheet());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.sheetOpen) this.closeSheet();
      });
      const onBreakpoint = () => {
        if (!this.mobileQuery.matches) this.closeSheet(true);
      };
      if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', onBreakpoint);
      else if (this.mobileQuery.addListener) this.mobileQuery.addListener(onBreakpoint);

      document.addEventListener('shopify:block:select', (e) => {
        const btn = this.el.prints.find((b) => b.dataset.print === e.detail.blockId);
        if (!btn) return;
        if (this.el.cats.length) this.selectCategory(btn.dataset.category, false, btn.dataset.print);
        this.selectPrint(e.detail.blockId, false, true);
      });

      const fromUrl = this.printFromUrl();
      if (this.el.cats.length) {
        const urlBtn = fromUrl && this.el.prints.find((b) => b.dataset.print === fromUrl);
        const wantedCat = (new URLSearchParams(window.location.search).get('collection') || '').toLowerCase();
        const catWithPrints = (c) => this.el.prints.some((b) => b.dataset.category === c && !b.disabled);
        let cat = urlBtn ? urlBtn.dataset.category : this.el.cats.some((b) => b.dataset.category === wantedCat) ? wantedCat : null;
        if (!cat) {
          const firstCat = this.el.cats.find((b) => catWithPrints(b.dataset.category)) || this.el.cats[0];
          cat = firstCat && firstCat.dataset.category;
        }
        this.selectCategory(cat, false, fromUrl);
      } else {
        const first = this.el.prints.find((b) => !b.disabled);
        const start = fromUrl || (first ? first.dataset.print : null);
        if (start) this.selectPrint(start, false);
      }
      this.renderSummary();
    }

    /* ---------- Data ---------- */

    getData(id) {
      if (this.data[id] !== undefined) return this.data[id];
      const node = this.querySelector(`script[data-print-data="${id}"]`);
      let parsed = null;
      if (node) {
        try {
          parsed = JSON.parse(node.textContent);
        } catch (e) {
          console.error('[hgg-bundle-builder] Bad data for print', id, e);
        }
      }
      this.data[id] = parsed;
      return parsed;
    }

    set() {
      return this.items;
    }

    count() {
      return this.set().reduce((n, l) => n + l.qty, 0);
    }

    /* ---------- Collection type (Seasonal / Evergreen) ---------- */

    selectCategory(cat, fromClick, preferPrint) {
      if (!cat) return;
      this.category = cat;

      this.el.cats.forEach((b) => {
        const on = b.dataset.category === cat;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      this.el.prints.forEach((b) => {
        b.hidden = b.dataset.category !== cat;
      });

      const inCat = (b) => b.dataset.category === cat;
      let target = preferPrint && this.el.prints.find((b) => b.dataset.print === preferPrint && inCat(b));
      if (!target) {
        const current = this.el.prints.find((b) => b.dataset.print === this.printId);
        target = current && inCat(current) && !current.disabled ? current : this.el.prints.find((b) => inCat(b) && !b.disabled);
      }
      if (target) this.selectPrint(target.dataset.print, false);

      if (fromClick) {
        const url = new URL(window.location.href);
        url.searchParams.set('collection', cat);
        if (target) {
          const handles = target.dataset.handles.split(',');
          const handle = handles[0] || handles[1];
          if (handle) url.searchParams.set('print', handle);
        }
        history.replaceState(history.state, '', url);
        if (window.matchMedia('(max-width: 999px)').matches && this.el.stepPrints) {
          this.el.stepPrints.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    onCatKey(e, btn) {
      const i = this.el.cats.indexOf(btn);
      let next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = this.el.cats[(i + 1) % this.el.cats.length];
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = this.el.cats[(i - 1 + this.el.cats.length) % this.el.cats.length];
      if (next) {
        e.preventDefault();
        next.focus();
        this.selectCategory(next.dataset.category, true);
      }
    }

    /* ---------- Print step ---------- */

    printFromUrl() {
      const wanted = (new URLSearchParams(window.location.search).get('print') || '').toLowerCase().trim();
      if (!wanted) return null;
      const match = this.el.prints.find((b) => !b.disabled && b.dataset.handles.split(',').includes(wanted));
      return match ? match.dataset.print : null;
    }

    selectPrint(id, fromClick, force) {
      const btn = this.el.prints.find((b) => b.dataset.print === id);
      if (!btn || (btn.disabled && !force)) return;

      this.printId = id;
      this.group = 'all';
      this.picked = {};

      this.el.prints.forEach((b) => {
        const on = b === btn;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });

      const name = btn.dataset.name || '';
      this.el.current.textContent = name ? '· ' + name : '';
      this.el.summaryPrint.textContent = '';
      this.el.prompt.hidden = true;

      this.renderTabs();
      this.renderProducts();
      this.renderSummary();

      if (fromClick) {
        const handles = btn.dataset.handles.split(',');
        const handle = handles[0] || handles[1];
        if (handle) {
          const url = new URL(window.location.href);
          url.searchParams.set('print', handle);
          history.replaceState(history.state, '', url);
        }
        if (window.matchMedia('(max-width: 999px)').matches) {
          this.el.step2.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    onPrintKey(e, btn) {
      const enabled = this.el.prints.filter((b) => !b.disabled && !b.hidden);
      const i = enabled.indexOf(btn);
      let next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = enabled[(i + 1) % enabled.length];
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = enabled[(i - 1 + enabled.length) % enabled.length];
      if (next) {
        e.preventDefault();
        next.focus();
        this.selectPrint(next.dataset.print, true);
      }
    }

    /* ---------- Step 2 ---------- */

    renderTabs() {
      const data = this.getData(this.printId);
      if (!data) {
        this.el.tabs.hidden = true;
        return;
      }
      const present = new Set(data.products.map((p) => p.g));
      const groups = data.groups
        .map((label, i) => ({ label, i }))
        .filter((g) => g.label && present.has(g.i));

      if (groups.length < 2) {
        this.el.tabs.hidden = true;
        this.group = 'all';
        return;
      }

      const tabs = [{ label: this.t.all || 'Everyone', i: 'all' }].concat(groups);
      this.el.tabs.innerHTML = tabs
        .map((g) => {
          const on = String(this.group) === String(g.i);
          return `<button type="button" role="tab" class="hgg-bb__tab" data-group="${g.i}" aria-selected="${on}">${esc(g.label)}</button>`;
        })
        .join('');
      this.el.tabs.hidden = false;
    }

    renderProducts() {
      const data = this.getData(this.printId);
      if (!data) {
        this.el.products.innerHTML = '';
        return;
      }

      const list = data.products.filter((p) => this.group === 'all' || String(p.g) === String(this.group));
      if (!list.length) {
        this.el.products.innerHTML = `<p class="hgg-bb__prompt">${esc(this.t.noProducts || '')}</p>`;
        return;
      }

      const groupLabel = (g) => (data.groups[g] || '');

      this.el.products.innerHTML = list
        .map((p) => {
          const priced = p.v.filter((v) => v.a);
          const ref = priced[0] || p.v[0];
          const minPrice = Math.min(...(priced.length ? priced : p.v).map((v) => v.p));
          const compare = ref && ref.c > ref.p ? ref.c : 0;
          const picked = this.picked[p.id];

          const sizes = p.v
            .map((v) => {
              const on = String(picked) === String(v.id);
              return `<button type="button" class="hgg-bb__size" data-size="${v.id}" data-product="${p.id}" aria-pressed="${on}" ${v.a ? '' : 'disabled'}>${esc(v.s)}</button>`;
            })
            .join('');

          return `
            <div class="hgg-bb__card" data-card="${p.id}">
              <button type="button" class="hgg-bb__card-media" data-qv="${esc(p.url)}" aria-label="${esc(p.title)}: ${esc(this.t.details || 'View details')}" aria-haspopup="dialog">
                ${p.img ? `<img src="${esc(p.img)}" alt="${esc(p.title)}" loading="lazy" width="480" height="600">` : ''}
              </button>
              ${groupLabel(p.g) ? `<span class="hgg-bb__card-who">${esc(groupLabel(p.g))}</span>` : ''}
              <button type="button" class="hgg-bb__card-title" data-qv="${esc(p.url)}" aria-haspopup="dialog">${esc(p.title)}</button>
              <span class="hgg-bb__card-price">
                ${compare ? `<s>${this.money(compare)}</s> <span class="hgg-bb__card-sale">${this.money(minPrice)}</span>` : this.money(minPrice)}
              </span>
              <div class="hgg-bb__sizes" role="group" aria-label="Size">${sizes}</div>
              <button type="button" class="hgg-btn hgg-btn--outline hgg-bb__card-add" data-add-piece="${p.id}">${esc(this.t.addPiece || 'Add')}</button>
              <span class="hgg-bb__card-msg" data-card-msg="${p.id}" aria-live="polite"></span>
            </div>`;
        })
        .join('');
    }

    onProductsClick(e) {
      const qv = e.target.closest('[data-qv]');
      if (qv) {
        this.openQuickView(qv.dataset.qv, qv);
        return;
      }

      const size = e.target.closest('[data-size]');
      if (size) {
        const pid = size.dataset.product;
        this.picked[pid] = size.dataset.size;
        const card = size.closest('[data-card]');
        card.querySelectorAll('[data-size]').forEach((b) => b.setAttribute('aria-pressed', b === size ? 'true' : 'false'));
        card.querySelector('[data-card-msg]').textContent = '';
        return;
      }

      const add = e.target.closest('[data-add-piece]');
      if (add) this.addPiece(add.dataset.addPiece, add.closest('[data-card]'));
    }

    addPiece(productId, card) {
      const msg = card.querySelector('[data-card-msg]');
      const data = this.getData(this.printId);
      const product = data && data.products.find((p) => String(p.id) === String(productId));
      if (!product) return;

      let variantId = this.picked[productId];
      // Single-size products don't need a pick
      if (!variantId) {
        const available = product.v.filter((v) => v.a);
        if (available.length === 1 && product.v.length === 1) variantId = available[0].id;
      }
      if (!variantId) {
        msg.textContent = this.t.pickSize || 'Pick a size first';
        return;
      }
      if (this.count() >= this.cfg.max) {
        msg.textContent = this.t.max || '';
        return;
      }

      const variant = product.v.find((v) => String(v.id) === String(variantId));
      const lines = this.set();
      const existing = lines.find((l) => String(l.variantId) === String(variantId));
      if (existing) existing.qty += 1;
      else
        lines.push({
          key: String(variantId),
          variantId: variant.id,
          printId: this.printId,
          print: data.name || '',
          product: product.title,
          size: variant.s,
          variantTitle: variant.t,
          price: variant.p,
          compare: variant.c,
          img: product.img,
          qty: 1
        });

      msg.textContent = '';
      card.classList.remove('is-added');
      void card.offsetWidth;
      card.classList.add('is-added');
      this.renderSummary();
    }

    removeLine(key) {
      const lines = this.set();
      const i = lines.findIndex((l) => l.key === key);
      if (i === -1) return;
      if (lines[i].qty > 1) lines[i].qty -= 1;
      else lines.splice(i, 1);
      this.renderSummary();
    }

    /* ---------- Summary ---------- */

    tierFor(count) {
      let current = null;
      let next = null;
      this.cfg.tiers.forEach((t) => {
        if (count >= t.qty) current = t;
        else if (!next) next = t;
      });
      return { current, next };
    }

    renderSummary() {
      const lines = this.set();
      const count = this.count();
      const subtotal = lines.reduce((sum, l) => sum + l.price * l.qty, 0);
      const { current, next } = this.tierFor(count);

      const eligible = lines.reduce((sum, l) => {
        const onSale = l.compare > l.price;
        return sum + (this.cfg.tierOnSale || !onSale ? l.price * l.qty : 0);
      }, 0);
      const savings = current ? Math.round((eligible * current.pct) / 100) : 0;
      const total = subtotal - savings;

      this.el.lines.innerHTML = lines
        .map(
          (l) => `
          <li class="hgg-bb__line">
            <span class="hgg-bb__line-media">${l.img ? `<img src="${esc(l.img)}" alt="" loading="lazy" width="80" height="100">` : ''}</span>
            <span class="hgg-bb__line-info">
              <span class="hgg-bb__line-title">${esc(l.product)}</span>
              <span class="hgg-bb__line-meta">${l.print ? esc(l.print) + ' · ' : ''}${esc(l.size)}${l.qty > 1 ? ` · ×${l.qty}` : ''}</span>
              <button type="button" class="hgg-bb__line-remove" data-remove="${esc(l.key)}">${esc(this.t.remove || 'Remove')}</button>
            </span>
            <span class="hgg-bb__line-price">${
              l.compare > l.price
                ? `<s>${this.money(l.compare * l.qty)}</s> <span class="hgg-bb__line-sale">${this.money(l.price * l.qty)}</span>`
                : this.money(l.price * l.qty)
            }</span>
          </li>`
        )
        .join('');

      this.el.empty.hidden = lines.length > 0;

      // Tier message
      if (this.cfg.tiers.length && count > 0) {
        const top = this.cfg.tiers[this.cfg.tiers.length - 1];
        let text = '';
        if (next) text = this.fill(this.t.tierNext, { count: next.qty - count, pct: next.pct });
        else if (current) text = this.fill(this.t.tierDone, { count, pct: current.pct });
        this.el.tierMsg.textContent = text;
        this.el.tierMsg.classList.toggle('is-done', !next);
        this.el.tierFill.style.width = Math.min(100, (count / top.qty) * 100) + '%';
        this.el.tier.hidden = !text;
      } else {
        this.el.tier.hidden = true;
      }

      this.el.saveRow.hidden = savings <= 0;
      this.el.save.textContent = savings > 0 ? '−' + this.money(savings) : '';
      this.el.total.textContent = this.money(total);

      // Sale savings: compare-at minus current price on pieces already marked down, plus any bundle savings
      const saleSavings = lines.reduce((sum, l) => sum + (l.compare > l.price ? (l.compare - l.price) * l.qty : 0), 0);
      const youSaved = saleSavings + savings;
      if (this.el.saved) {
        this.el.saved.hidden = youSaved <= 0;
        this.el.savedAmount.textContent = youSaved > 0 ? this.money(youSaved) : '';
      }

      const ready = count >= this.cfg.min && !this._busy;
      this.el.add.disabled = !ready;
      if (!this._busy) {
        this.el.addLabel.textContent =
          count > 0 && count < this.cfg.min ? this.fill(this.t.minPieces, { count: this.cfg.min }) : this.t.add || 'Add all to cart';
      }

      // Mobile bar
      this.el.bar.hidden = count === 0 || this.sheetOpen;
      if (count === 0 && this.sheetOpen) this.closeSheet();
      this.el.barInfo.textContent = `${this.fill(this.t.pieces, { count })} · ${this.money(total)}`;
      this.el.barBtn.disabled = this._busy;
    }

    /* ---------- Mobile bottom sheet ---------- */

    openSheet() {
      if (!this.mobileQuery.matches || this.sheetOpen || this.count() === 0) return;
      this.sheetOpen = true;
      this._lastFocus = document.activeElement;
      this.classList.add('is-sheet-open');
      this.el.summary.setAttribute('role', 'dialog');
      this.el.summary.setAttribute('aria-modal', 'true');
      if (this.el.overlay) this.el.overlay.hidden = false;
      document.documentElement.classList.add('hgg-bb-lock');
      this.el.bar.hidden = true;
      requestAnimationFrame(() => {
        this.el.summary.classList.add('is-open');
        if (this.el.overlay) this.el.overlay.classList.add('is-open');
        if (this.el.close) this.el.close.focus({ preventScroll: true });
      });
    }

    closeSheet(silent) {
      if (!this.sheetOpen) return;
      this.sheetOpen = false;
      this.el.summary.classList.remove('is-open');
      if (this.el.overlay) this.el.overlay.classList.remove('is-open');
      this.el.summary.removeAttribute('role');
      this.el.summary.removeAttribute('aria-modal');
      document.documentElement.classList.remove('hgg-bb-lock');
      const done = () => {
        this.classList.remove('is-sheet-open');
        if (this.el.overlay) this.el.overlay.hidden = true;
      };
      if (silent) done();
      else setTimeout(done, 300);
      this.el.bar.hidden = this.count() === 0;
      if (!silent && this._lastFocus && this._lastFocus.focus) this._lastFocus.focus({ preventScroll: true });
    }

    /* ---------- Cart ---------- */

    pieces() {
      const out = [];
      this.set().forEach((l) => {
        for (let i = 0; i < l.qty; i++) out.push(l);
      });
      return out;
    }

    label(line) {
      return this.cfg.labelFormat
        .replace(/\[product\]/g, line.product)
        .replace(/\[size\]/g, line.size)
        .replace(/\[variant\]/g, line.variantTitle);
    }

    bundleVariant(bundle, count) {
      const variants = bundle.variants.filter((v) => v.available);
      if (!variants.length) return null;
      if (variants.length === 1) return variants[0];
      // Pack-size variants, e.g. "4 pieces" or "Pack of 4"
      const re = new RegExp(`(^|\\D)${count}(\\D|$)`);
      return variants.find((v) => re.test(v.title)) || null;
    }

    buildItems() {
      const lines = this.set();
      // A Simple Bundles bundle only applies when every piece is from the same print
      const printIds = [...new Set(lines.map((l) => l.printId))];
      const data = printIds.length === 1 ? this.getData(printIds[0]) : null;

      if (this.cfg.mode === 'simple_bundles' && data && data.bundle) {
        const variant = this.bundleVariant(data.bundle, this.count());
        if (variant) {
          const pieces = this.pieces();
          return [
            {
              id: variant.id,
              quantity: 1,
              properties: {
                _bundle_selection: pieces.map((l) => this.label(l)).join(' <> ')
              }
            }
          ];
        }
        console.warn('[hgg-bundle-builder] No bundle variant for', this.count(), 'pieces. Adding separate items.');
      }

      return lines.map((l) => ({
        id: l.variantId,
        quantity: l.qty,
        properties: { _family_set: l.print || '' }
      }));
    }

    async addToCart() {
      if (this._busy || this.count() < this.cfg.min) return;
      this._busy = true;
      this.el.error.hidden = true;
      this.el.addLabel.textContent = this.t.adding || 'Adding...';
      this.el.add.disabled = true;
      this.el.barBtn.disabled = true;

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: this.buildItems() })
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.description || json.message || 'Cart error');

        const tv = window.themeVariables || {};
        if ((tv.settings && tv.settings.cartType === 'page') || !document.querySelector('cart-drawer')) {
          window.location.href = `${root}cart`;
          return;
        }

        const cart = await fetch(`${root}cart.js`).then((r) => r.json());
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, openMiniCart: true } }));

        this.items = [];
        this._busy = false;
        this.closeSheet(true);
        this.renderSummary();
        this.el.addLabel.textContent = this.t.added || 'Added to cart';
        setTimeout(() => this.renderSummary(), 2500);
      } catch (err) {
        console.error('[hgg-bundle-builder]', err);
        this._busy = false;
        this.el.error.textContent = (err && err.message && err.message !== 'Cart error' ? err.message + ' ' : '') + (this.t.error || '');
        this.el.error.hidden = false;
        this.renderSummary();
      }
    }

    /* ---------- Quick view pop-up ---------- */

    qvEl() {
      if (this._qv) return this._qv;
      const wrap = document.createElement('div');
      wrap.className = 'hgg-qv-modal';
      wrap.hidden = true;
      wrap.innerHTML = `
        <div class="hgg-qv-modal__overlay" data-qv-close></div>
        <div class="hgg-qv-modal__panel" role="dialog" aria-modal="true" aria-label="${esc(this.t.details || 'Product details')}" tabindex="-1">
          <button type="button" class="hgg-qv-modal__close" data-qv-close aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>
          </button>
          <div class="hgg-qv-modal__body" data-qv-body></div>
        </div>`;
      document.body.appendChild(wrap);
      const panel = wrap.querySelector('.hgg-qv-modal__panel');
      const body = wrap.querySelector('[data-qv-body]');

      wrap.addEventListener('click', (e) => {
        if (e.target.closest('[data-qv-close]')) {
          this.closeQuickView();
          return;
        }
        const thumb = e.target.closest('[data-qv-thumb]');
        if (thumb) {
          this.qvGoTo(parseInt(thumb.dataset.qvThumb, 10));
          return;
        }
      });

      wrap.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this.closeQuickView();
          return;
        }
        if (e.key !== 'Tab') return;
        const focusable = Array.from(
          panel.querySelectorAll('button:not([disabled]):not([hidden]), a[href], [tabindex]:not([tabindex="-1"])')
        ).filter((el) => el.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });

      this._qv = { wrap, panel, body, cache: {} };
      return this._qv;
    }

    async openQuickView(url, trigger) {
      if (!url) return;
      const qv = this.qvEl();
      this._qvTrigger = trigger || null;
      this._qvUrl = url;

      qv.body.innerHTML = '<div class="hgg-qv-modal__loading" aria-live="polite"><span></span></div>';
      qv.wrap.hidden = false;
      document.documentElement.classList.add('hgg-bb-lock');
      requestAnimationFrame(() => {
        qv.wrap.classList.add('is-open');
        qv.panel.focus({ preventScroll: true });
      });

      try {
        let html = qv.cache[url];
        if (!html) {
          const sep = url.indexOf('?') === -1 ? '?' : '&';
          const res = await fetch(`${url}${sep}view=hgg-quickview`, { credentials: 'same-origin' });
          if (!res.ok) throw new Error(res.status);
          html = await res.text();
          if (html.indexOf('data-qv-product') === -1) throw new Error('No quick view');
          qv.cache[url] = html;
        }
        if (this._qvUrl !== url || qv.wrap.hidden) return;
        qv.body.innerHTML = html;
        qv.body.scrollTop = 0;
        const title = qv.body.querySelector('.hgg-qv__title');
        if (title && title.id) qv.panel.setAttribute('aria-labelledby', title.id);
        this.qvBindSlides();
        this.qvLoadSizeChart();
      } catch (err) {
        console.error('[hgg-bundle-builder] Quick view', err);
        // Fall back to the product page in a new tab
        this.closeQuickView();
        window.open(url, '_blank', 'noopener');
      }
    }

    qvBindSlides() {
      const qv = this._qv;
      const slides = qv.body.querySelector('[data-qv-slides]');
      if (!slides) return;
      const current = qv.body.querySelector('[data-qv-current]');
      const thumbs = Array.from(qv.body.querySelectorAll('[data-qv-thumb]'));
      let ticking = false;
      slides.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const i = Math.round(slides.scrollLeft / (slides.clientWidth || 1));
          if (current) current.textContent = String(i + 1);
          thumbs.forEach((t, n) => {
            if (n === i) t.setAttribute('aria-current', 'true');
            else t.removeAttribute('aria-current');
          });
        });
      });
    }

    // Size chart from the Kiwi Sizing app, same as the product page
    qvLoadSizeChart() {
      const node = this._qv.body.querySelector('[data-qv-kiwi]');
      if (!node) return;
      let data;
      try {
        data = JSON.parse(node.textContent);
      } catch (e) {
        return;
      }
      data.waitForElementSelector = '';
      data.waitForElementDelay = 5;
      data.fallbackToBlockPosition = true;
      window.KiwiSizing = window.KiwiSizing || {};
      window.KiwiSizing.shop = node.dataset.shop;
      window.KiwiSizing.data = data;

      const run = () => {
        try {
          if (window.ks && typeof window.ks.cleanup === 'function') window.ks.cleanup();
          if (typeof window.loadKiwiSizingChart === 'function') window.loadKiwiSizingChart();
          else if (window.ks && typeof window.ks.loadSizing === 'function') window.ks.loadSizing({ productData: data, options: {} });
        } catch (e) {
          console.warn('[hgg-bundle-builder] Size chart', e);
        }
      };

      if (window.ks && typeof window.ks.loadSizing === 'function') {
        run();
        return;
      }
      if (this._kiwiLoading) {
        // Script still loading for an earlier product: run again once it's ready
        this._kiwiLoading.then(run).catch(() => {});
        return;
      }
      // First load: the plugin reads window.KiwiSizing.data on its own
      this._kiwiLoading = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = node.dataset.src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      this._kiwiLoading.catch(() => {});
    }

    qvGoTo(i) {
      const slides = this._qv && this._qv.body.querySelector('[data-qv-slides]');
      if (!slides) return;
      slides.scrollTo({ left: i * slides.clientWidth, behavior: 'smooth' });
    }

    closeQuickView() {
      const qv = this._qv;
      if (!qv || qv.wrap.hidden) return;
      this._qvUrl = null;
      qv.wrap.classList.remove('is-open');
      if (!this.sheetOpen) document.documentElement.classList.remove('hgg-bb-lock');
      setTimeout(() => {
        qv.wrap.hidden = true;
        qv.body.innerHTML = '';
      }, 250);
      if (this._qvTrigger && this._qvTrigger.isConnected) this._qvTrigger.focus({ preventScroll: true });
    }

    /* ---------- Helpers ---------- */

    fill(str, vars) {
      return String(str || '').replace(/\[(\w+)\]/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
    }

    money(cents) {
      const value = (cents || 0) / 100;
      const fmt = this.cfg.moneyFormat;
      const withDelims = (n, decimals, thousands, decimal) => {
        const parts = n.toFixed(decimals).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.join(decimal);
      };
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return withDelims(value, 0, ',', '.');
          case 'amount_with_comma_separator':
            return withDelims(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return withDelims(value, 0, '.', ',');
          default:
            return withDelims(value, 2, ',', '.');
        }
      });
    }
  }

  customElements.define('hgg-bundle-builder', HggBundleBuilder);
})();
